import React, {
  useState,
  useEffect,
  useRef,
} from 'react';
import { connect } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import _ from 'lodash';
import { Loader as LoaderLayoutComponent } from '@playbookux/playbook-web-core';
import FilterModal from './filter-modal';

import withProjectSidebar from '../../../components/with-project-sidebar';
import Tabs from '../../../constants/tabs';

import SurveyPagesResults from './survey-pages-results';
import { GET_ANALYTICS_SURVEY, GET_SAVED_FILTERS } from './graphql';

import {
  Message,
  Dropdown,
  Loader,
  ContainerSurveyPage,
  FiltersRow,
  LoaderContainer,
  FilterLabel,
} from './styles';
import EmptyResult from '../components/empty-result';
import EmptyResultSegment from '../components/empty-segment';

const ProjectAnalyticsSurvey = ({ project, scope }) => {
  const { approvedTestersNumber } = project;

  const [segmentSelected, setSegmentSelected] = useState('all');
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [savedFilters, setSavedFilters] = useState([]);
  const [customFilters, setCustomFilters] = useState([]);

  const previousSavedFiltersCountRef = useRef(0);

  const { data: savedFiltersData } = useQuery(GET_SAVED_FILTERS, {
    variables: { projectId: project.id },
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (savedFiltersData && savedFiltersData.analyticsFilters) {
      setSavedFilters(savedFiltersData.analyticsFilters);
    }
  }, [savedFiltersData]);

  const {
    loading,
    data = {},
    errors,
    refetch,
  } = useQuery(
    GET_ANALYTICS_SURVEY, {
      variables: { projectId: project.id },
      fetchPolicy: 'no-cache',
    },
  );

  const refetchAnalytics = (variables) => {
    const {
      segmentId = null,
      conditions = null,
      analyticsFilterId = null,
    } = variables || {};

    const unsavedConditions = !_.isEmpty(conditions) ? JSON.stringify(conditions) : null;

    refetch({
      projectId: project.id,
      segmentId,
      analyticsFilterId,
      unsavedConditions,
    });
  };

  // apply a custom (unsaved) filter
  const applyUnsavedFilter = ({ name, conditions }) => {
    const id = uuidv4();

    setCustomFilters((prev) => [
      ...prev,
      { id, name, conditions },
    ]);

    setSegmentSelected(`custom-filter-${id}`);
    refetchAnalytics({ conditions });
  };

  // apply a saved filter
  const applySavedFilter = (filter) => {
    setSegmentSelected(`filter-${filter.id}`);
    refetchAnalytics({ analyticsFilterId: filter.id });
  };

  const handleSelectedFilter = (filter) => {
    if (filter.saved) {
      applySavedFilter(filter);
    } else {
      applyUnsavedFilter(filter);
    }
  };

  const handleDropdownChange = (event, { value }) => {
    if (value === segmentSelected) return;

    if (value === 'new-filter') {
      setIsFilterModalOpen(true);
      return;
    }

    setSegmentSelected(value);

    const getId = (prefix) => value.replace(prefix, '');

    switch (true) {
      case value.startsWith('filter-'):
        refetchAnalytics({ analyticsFilterId: getId('filter-') });
        break;

      case value.startsWith('custom-filter-'): {
        const customFilter = customFilters.find((f) => f.id === getId('custom-filter-'));
        refetchAnalytics({ conditions: customFilter?.conditions });
        break;
      }

      case value !== 'all': {
        refetchAnalytics({ segmentId: value });
        break;
      }

      default:
        refetchAnalytics();
        break;
    }
  };

  const { analytics: { surveysAnalytics = [] } = {} } = data;

  // reset selected dropdown item when a filter is deleted
  useEffect(() => {
    const previousCount = previousSavedFiltersCountRef.current;
    const currentCount = savedFilters.length;
    previousSavedFiltersCountRef.current = currentCount;

    if (currentCount < previousCount) {
      if (segmentSelected.startsWith('filter-')) {
        const currentFilterId = segmentSelected.replace('filter-', '');
        const exists = savedFilters.some((filter) => filter.id === currentFilterId);
        if (!exists) {
          // Reset to all if the current filter was deleted
          setSegmentSelected('all');
        }
      }
    }
  }, [savedFilters, segmentSelected]);

  if (approvedTestersNumber === 0) {
    return (
      <EmptyResult testType="survey" />
    );
  }

  if (loading) {
    return <Loader data-loader="project-loader" />;
  }

  if (errors) {
    return (
      <Message>
        Error while fetching analytics.
      </Message>
    );
  }

  const dropdownOptions = [
    { key: 'all', text: 'All audience segments', value: 'all' },
    ..._.map(project.segments, (segment) => ({
      key: segment.id,
      text: segment.name,
      value: segment.id,
    })),
    ...savedFilters.map((filter) => ({
      key: `filter-${filter.id}`,
      text: filter.name,
      value: `filter-${filter.id}`,
    })),
    ...customFilters.map((filter) => ({
      key: `custom-filter-${filter.id}`,
      text: filter.name,
      value: `custom-filter-${filter.id}`,
    })),
    {
      key: 'new-filter',
      text: 'Create new filter',
      value: 'new-filter',
      className: 'create-new-filter-option',
    },
  ];

  return (
    <ContainerSurveyPage>
      <FiltersRow>
        {project.segments.length >= 1 && (
          <>
            <FilterLabel>Filter results by</FilterLabel>
            <Dropdown
              placeholder="Select one segment"
              fluid
              selection
              value={segmentSelected}
              onChange={handleDropdownChange}
              options={dropdownOptions}
            />
          </>
        )}
      </FiltersRow>

      {isFilterModalOpen && (
        <FilterModal
          open={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          projectId={project.id}
          onFilterApplied={handleSelectedFilter}
          scope={scope}
          participantsSource={project.participantsSource}
        />
      )}

      {!_.isEmpty(surveysAnalytics) && surveysAnalytics.map((surveyPageResults) => (
        <SurveyPagesResults
          key={surveyPageResults.page}
          projectTestType={project.testType}
          surveyPageResults={surveyPageResults}
          segmentSelected={
            (segmentSelected === 'all' || segmentSelected.startsWith('filter-') || segmentSelected.startsWith('custom-filter-'))
              ? null
              : segmentSelected
          }
        />
      ))}
      {loading && (
        <LoaderContainer>
          <LoaderLayoutComponent data-loader="project-analytics-survey-loader" />
        </LoaderContainer>
      )}
      {(surveysAnalytics && surveysAnalytics.length === 0) && (
        <EmptyResultSegment />
      )}
    </ContainerSurveyPage>
  );
};

ProjectAnalyticsSurvey.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    segments: PropTypes.arrayOf(PropTypes.shape()),
    approvedTestersNumber: PropTypes.number,
    participantsSource: PropTypes.string,
    surveyPagesCount: PropTypes.number,
    testType: PropTypes.string,
  }).isRequired,
  scope: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => ({
  scope: state.authentication && state.authentication.scope,
});

export default compose(
  withRouter,
  withProjectSidebar({ selectedTab: Tabs.PROJECT }),
  connect(mapStateToProps),
)(ProjectAnalyticsSurvey);
